import type { DiagnosisIdentification } from '@/lib/types/diagnosis'

/**
 * Free-text diagnosis problem name -> canonical condition -> a live artifact.
 *
 * The model names a condition in prose and the same condition arrives under
 * many spellings ("Mowing injury from dull mower blade", "Ragged mowing injury
 * (likely dull mower blade)", "Torn grass tips from dull mower blade"). Only
 * `weed` findings carry a knowledge-base slug that a client can route on, so
 * every environmental and disease finding needs its identity recovered from the
 * name itself.
 *
 * Deliberately client-safe: no editorial data, no knowledge-base imports. This
 * module is bundled into the diagnosis result screen.
 *
 * Every destination is an informational guide or a free calculator. Chemical
 * and product guidance stays server-owned behind verified host grass and must
 * never be routed from here.
 */

export type DiagnosisConditionId =
  | 'drought-heat-stress'
  | 'mowing-injury'
  | 'thin-turf'
  | 'compaction'
  | 'thatch'
  | 'brown-patch'

export interface DiagnosisNextStep {
  conditionId: DiagnosisConditionId
  href: string
  label: string
}

/**
 * Verified live 2026-08-09: each blog slug is `status: published` in Directus
 * and each route returns 200 in production.
 */
export const DIAGNOSIS_CONDITION_NEXT_STEPS: Record<DiagnosisConditionId, DiagnosisNextStep> = {
  'drought-heat-stress': {
    conditionId: 'drought-heat-stress',
    href: '/blog/is-my-grass-dead-or-dormant',
    label: 'Read the dead or dormant grass guide',
  },
  'mowing-injury': {
    conditionId: 'mowing-injury',
    href: '/blog/how-to-sharpen-lawn-mower-blades',
    label: 'Read the mower blade sharpening guide',
  },
  'thin-turf': {
    conditionId: 'thin-turf',
    href: '/blog/how-to-fix-bare-spots-in-your-lawn-before-winter',
    label: 'Read the bare and thin spot repair guide',
  },
  compaction: {
    conditionId: 'compaction',
    href: '/tools/aeration-calculator',
    label: 'Open the free aeration calculator',
  },
  thatch: {
    conditionId: 'thatch',
    href: '/tools/dethatching-calculator',
    label: 'Open the free dethatching calculator',
  },
  'brown-patch': {
    conditionId: 'brown-patch',
    href: '/blog/brown-patch-prevention',
    label: 'Read the full brown patch prevention guide',
  },
}

/**
 * Lowercase, collapse every non-alphanumeric run to a single space, and pad
 * with one leading space. Phrases are then tested with `includes(' ' + phrase)`,
 * so a phrase must start on a word boundary but may end mid-word. That is what
 * lets `mower blade` match "mower blades" and `compact` match "compaction"
 * without a separate stemmer.
 */
export function normalizeProblemText(text: string): string {
  return ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()}`
}

interface ConditionRule {
  conditionId: DiagnosisConditionId
  /** Problem types this rule may fire for. Measured against 365 days of live
      results: every one of these conditions arrives as `environmental`, except
      brown/large patch which arrives as `disease`. */
  types: DiagnosisIdentification['type'][]
  phrases: string[]
}

/**
 * Ordered by mechanism specificity, not by volume. First match wins, so a name
 * that carries two signals resolves to the more specific one: "Scalping / thin
 * turf from low mowing" is a mowing problem, not a thin-turf problem.
 * `drought` sits above `thin-turf` for the same reason, since roughly two
 * thirds of thinning names name drought or heat as their cause.
 */
const CONDITION_RULES: ConditionRule[] = [
  {
    conditionId: 'mowing-injury',
    types: ['environmental'],
    // Bare "mowing" is deliberately absent: "uneven color from mowing
    // direction, not disease" is a mowing observation, not a mowing injury.
    phrases: [
      'mowing injury',
      'mowing damage',
      'mowing stress',
      'mower blade',
      'dull blade',
      'scalp',
      'mowed too low',
      'low mowing',
      'low cut',
      'torn grass tip',
      'ragged cut',
    ],
  },
  {
    conditionId: 'thatch',
    types: ['environmental'],
    phrases: ['thatch'],
  },
  {
    conditionId: 'brown-patch',
    types: ['disease'],
    phrases: ['brown patch', 'large patch'],
  },
  {
    conditionId: 'drought-heat-stress',
    types: ['environmental'],
    // No bare "water": "waterlogging" and "overwatering" are the opposite
    // problem and must not route to a drought guide.
    phrases: [
      'drought',
      'heat stress',
      'heat and drought',
      'dormanc',
      'desiccat',
      'water stress',
      'underwater',
      'uneven moisture',
      'uneven irrigation',
      'inadequate irrigation',
      'insufficient irrigation',
    ],
  },
  {
    conditionId: 'compaction',
    types: ['environmental'],
    phrases: ['compact', 'traffic damage', 'foot traffic'],
  },
  {
    conditionId: 'thin-turf',
    types: ['environmental'],
    phrases: [
      'thin turf',
      'thinning',
      'dieback',
      'die back',
      'bare soil',
      'bare spot',
      'bare patch',
      'poor establishment',
      'sparse',
    ],
  },
]

/**
 * Knowledge-base slugs that already resolve server-side. Checked before the
 * prose rules so a finding that did get enriched routes on its stable identity
 * rather than on whatever the model happened to call it.
 */
const SLUG_TO_CONDITION: Record<string, DiagnosisConditionId> = {
  drought: 'drought-heat-stress',
  heat: 'drought-heat-stress',
  scalping: 'mowing-injury',
  compaction: 'compaction',
  'brown-patch': 'brown-patch',
  'large-patch': 'brown-patch',
}

/**
 * Canonical condition for a diagnosis finding, or null when nothing matches
 * confidently. Null is the safe answer: an unmatched finding keeps today's
 * behavior instead of receiving a link to content about a different problem.
 */
export function resolveDiagnosisConditionId(
  problem: Pick<DiagnosisIdentification, 'type' | 'name' | 'matchedSlug'> | undefined | null,
): DiagnosisConditionId | null {
  if (!problem) return null

  const slugMatch = problem.matchedSlug ? SLUG_TO_CONDITION[problem.matchedSlug] : undefined
  if (slugMatch) return slugMatch

  const name = typeof problem.name === 'string' ? problem.name : ''
  if (!name.trim()) return null

  const normalized = normalizeProblemText(name)

  for (const rule of CONDITION_RULES) {
    if (!rule.types.includes(problem.type)) continue
    if (rule.phrases.some((phrase) => normalized.includes(` ${phrase}`))) {
      return rule.conditionId
    }
  }

  return null
}

/** The verified destination for a finding, or null when it does not normalize. */
export function getDiagnosisNextStep(
  problem: Pick<DiagnosisIdentification, 'type' | 'name' | 'matchedSlug'> | undefined | null,
): DiagnosisNextStep | null {
  const conditionId = resolveDiagnosisConditionId(problem)
  return conditionId ? DIAGNOSIS_CONDITION_NEXT_STEPS[conditionId] : null
}
